import { ColumnTable, Icon, addToast } from '@octano/global-ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UncontrolledTooltip } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { updateStudentCredits } from '../api';
import { StudentLiquidationResult } from '../types';

const prefix = (key: string) => `table.${key}`;

interface UseColumnsProps {
  periodId: number;
  onCreditsUpdated?: () => void;
}

export default function useColumns({
  periodId,
  onCreditsUpdated,
}: UseColumnsProps): ColumnTable<StudentLiquidationResult>[] {
  const { t } = useTranslation(TranslationsKeys.CREDIT_LIQUIDATION_INITIAL);
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  return [
    {
      columnName: 'studentNameAndId',
      headerText: t(prefix('studentNameAndId')),
      thClassName: 'text-left',
      tdClassName: 'text-left',
      cellFormat: ({ row }) => (
        <div>
          <div>{row.studentName}</div>
          <small className="text-muted">
            {row.source === 'postulant' ? 'Postulante' : row.run || '-'}
          </small>
        </div>
      ),
    },
    {
      columnName: 'schoolName',
      headerText: t(prefix('school')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
    },
    {
      columnName: 'studyPlanName',
      headerText: t(prefix('studyPlan')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
    },
    {
      columnName: 'campusName',
      headerText: t(prefix('campus')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
    },
    {
      columnName: 'scheduleName',
      headerText: t(prefix('schedule')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
    },
    {
      columnName: 'modalityName',
      headerText: t(prefix('modality')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => <span>{row.modalityName || '-'}</span>,
    },
    {
      columnName: 'assignedLevel',
      headerText: (
        <span className="d-flex align-items-center justify-content-center gap-1">
          {t(prefix('assignedLevel'))}
          <Icon name="information" size="14px" color="primary" />
        </span>
      ),
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => (
        <span className="fw-bold text-primary">
          {row.assignedLevel} {t(prefix('semester'))}
        </span>
      ),
    },
    {
      columnName: 'recommendedCredits',
      headerText: t(prefix('recommendedCredits')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => (
        <span className="fw-bold text-success">{row.recommendedCredits}</span>
      ),
    },
    {
      columnName: 'creditsToEnroll',
      headerText: t(prefix('creditsToEnroll')),
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => {
        const tooltipId = `credits-tooltip-${row.studyPlanEnrollmentId}`;
        const hasPetition = row.petitionId;
        const hasChange = row.lastModifiedBy;
        const displayCredits = row.currentCredits ?? row.recommendedCredits;

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <input
              type="number"
              className="form-control form-control-sm text-center"
              defaultValue={displayCredits}
              min={0}
              style={{ width: '80px' }}
              ref={(el) => {
                const key =
                  row.studyPlanEnrollmentId ??
                  `postulant-${row.postulationDetailId}`;
                inputRefs.current[key as number] = el;
              }}
            />
            {(hasPetition || hasChange) && (
              <>
                <span id={tooltipId} className="text-info cursor-pointer">
                  <Icon name="information" size="18px" />
                </span>
                <UncontrolledTooltip placement="top" target={tooltipId}>
                  {hasPetition && (
                    <div>
                      {t(prefix('petitionTooltip'), { id: row.petitionId })}
                    </div>
                  )}
                  {hasChange && (
                    <div>
                      {t(prefix('changeTooltip'), {
                        name: row.lastModifiedBy,
                        date: row.lastModifiedAt,
                      })}
                    </div>
                  )}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        );
      },
    },
    {
      columnName: 'actions',
      headerText: '',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      cellFormat: ({ row }) => {
        const handleUpdateCredits = async () => {
          const key =
            row.studyPlanEnrollmentId ?? `postulant-${row.postulationDetailId}`;
          const inputElement = inputRefs.current[key as number];
          if (!inputElement) {
            return;
          }

          const newCredits = parseInt(inputElement.value, 10);
          if (isNaN(newCredits) || newCredits < 0) {
            addToast({
              icon: 'error',
              color: 'danger',
              text: t('updateCredits.invalidValue'),
            });
            return;
          }

          try {
            const response = await updateStudentCredits({
              periodId,
              studyPlanEnrollmentId: row.studyPlanEnrollmentId ?? undefined,
              postulationDetailId: row.postulationDetailId,
              creditsContracted: newCredits,
            });

            if (response.data?.success) {
              addToast({
                icon: 'success',
                color: 'success',
                text: t('updateCredits.success'),
              });
              onCreditsUpdated?.();
            } else {
              addToast({
                icon: 'error',
                color: 'danger',
                text: response.data?.message || t('updateCredits.error'),
              });
            }
          } catch (error) {
            console.error('Error updating credits:', error);
            addToast({
              icon: 'error',
              color: 'danger',
              text: t('updateCredits.error'),
            });
          }
        };

        return (
          <button
            type="button"
            className="btn btn-link p-0 text-primary"
            title={t(prefix('editCredits'))}
            onClick={handleUpdateCredits}
          >
            <Icon name="edit" size="18px" />
          </button>
        );
      },
    },
  ];
}
